// components/RecordingStudio/VoiceSelector.jsx
import React, { useState, useEffect, useRef } from "react";
import { X, Play, Pause, Search, Heart, Star } from "lucide-react";
import api from "../../services/api";

// ─── Tier badge config ────────────────────────────────────────────────────────

const TIER_BADGE = {
  chirp3: {
    label: "Chirp 3 HD",
    className: "bg-violet-500/20 text-violet-300 border-violet-500/30",
  },
  chirp: {
    label: "Chirp HD",
    className: "bg-blue-500/20 text-blue-300 border-blue-500/30",
  },
  studio: {
    label: "Studio",
    className: "bg-amber-500/20 text-amber-300 border-amber-500/30",
  },
  neural2: {
    label: "Neural2",
    className: "bg-emerald-500/20 text-emerald-300 border-emerald-500/30",
  },
  news: {
    label: "News",
    className: "bg-sky-500/20 text-sky-300 border-sky-500/30",
  },
  wavenet: {
    label: "WaveNet",
    className: "bg-teal-500/20 text-teal-300 border-teal-500/30",
  },
  standard: {
    label: "Standard",
    className: "bg-gray-500/20 text-gray-400 border-gray-500/30",
  },
};

// ─── VoiceCard ────────────────────────────────────────────────────────────────

const VoiceCard = ({ voice, onSelect, playingId, onTogglePlay }) => {
  const isPlaying = playingId === voice.id;
  const tier = TIER_BADGE[voice.tier] || TIER_BADGE.standard;

  return (
    <div
      className="flex flex-col items-center p-4 rounded-xl border border-[#22262F] hover:border-purple-500/60 transition-all cursor-pointer group bg-[#0C0E12]/40 hover:bg-[#0C0E12]/80"
      onClick={() => onSelect(voice)}
    >
      {/* Avatar */}
      <div className="relative mb-3">
        <div className="w-20 h-20 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center overflow-hidden ring-2 ring-transparent group-hover:ring-purple-500/40 transition-all">
          {voice.avatarImageUrl ? (
            <img
              src={voice.avatarImageUrl}
              alt={voice.displayName}
              className="w-full h-full object-cover"
              onError={(e) => {
                // Fallback to initials if image fails
                e.target.style.display = "none";
                e.target.nextSibling.style.display = "flex";
              }}
            />
          ) : null}
          <span
            className="text-2xl font-bold text-white"
            style={{ display: voice.avatarImageUrl ? "none" : "flex" }}
          >
            {voice.displayName[0]}
          </span>
        </div>

        {/* Premium star */}
        {voice.isPremium && (
          <div className="absolute -top-1 -left-1 w-5 h-5 bg-amber-500 rounded-full flex items-center justify-center">
            <Star size={10} fill="white" className="text-white" />
          </div>
        )}

        {/* Play sample button */}
        {voice.sampleAudioUrl && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onTogglePlay(voice);
            }}
            className="absolute -bottom-1 -right-1 w-8 h-8 bg-purple-600 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-purple-700 shadow-lg"
          >
            {isPlaying ? (
              <Pause size={12} fill="white" className="text-white" />
            ) : (
              <Play size={12} fill="white" className="text-white" />
            )}
          </button>
        )}
      </div>

      {/* Name */}
      <p className="font-semibold text-white text-sm text-center leading-tight mb-1">
        {voice.displayName}
      </p>

      {/* Gender + accent */}
      <p className="text-xs text-gray-500 capitalize mb-2">
        {voice.gender} · {voice.accent || "British"}
      </p>

      {/* Tier badge */}
      <span
        className={`text-[10px] font-medium px-2 py-0.5 rounded-full border mb-2 ${tier.className}`}
      >
        {tier.label}
      </span>

      {/* Characteristics */}
      {voice.characteristics?.length > 0 && (
        <div className="flex flex-wrap gap-1 justify-center">
          {voice.characteristics.slice(0, 2).map((c) => (
            <span
              key={c}
              className="text-[10px] px-1.5 py-0.5 bg-[#22262F] text-gray-400 rounded"
            >
              {c}
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

// ─── VoiceSelector ────────────────────────────────────────────────────────────

const LANGUAGES = [
  { label: "English (UK)", code: "en-GB" },
  { label: "English (US)", code: "en-US" },
  { label: "Spanish (Spain)", code: "es-ES" },
  { label: "French", code: "fr-FR" },
  { label: "German", code: "de-DE" },
];

const VoiceSelector = ({ onSelect, onClose }) => {
  const [voices, setVoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);

  // Filters
  const [langCode, setLangCode] = useState("en-GB");
  const [gender, setGender] = useState("");
  const [tier, setTier] = useState("");
  const [isPremium, setIsPremium] = useState("");
  const [search, setSearch] = useState("");

  // Audio preview
  const [playingId, setPlayingId] = useState(null);
  const audioRef = useRef(null);

  // ── Load voices from DB via API ─────────────────────────────────────────────

  useEffect(() => {
    const controller = new AbortController();
    loadVoices(controller.signal);
    return () => controller.abort();
  }, [langCode, gender, tier, isPremium, search]);

  const loadVoices = async (signal) => {
    setLoading(true);
    try {
      const params = { languageCode: langCode, limit: 200 };
      if (gender) params.gender = gender;
      if (tier) params.tier = tier;
      if (isPremium) params.isPremium = isPremium;
      if (search) params.search = search;

      const response = await api.getVoices(params.languageCode, params);

      // api.getVoices already returns the grouped object; we use the flat `avatars`
      // array that the updated controller now also returns.
      setVoices(response.avatars || []);
      setTotal(response.total || 0);
    } catch (err) {
      if (err.name !== "AbortError") {
        console.error("Failed to load voices:", err);
      }
    } finally {
      setLoading(false);
    }
  };

  // ── Audio preview ───────────────────────────────────────────────────────────

  const handleTogglePlay = (voice) => {
    if (!voice.sampleAudioUrl) return;

    if (playingId === voice.id) {
      audioRef.current?.pause();
      setPlayingId(null);
      return;
    }

    if (audioRef.current) {
      audioRef.current.pause();
    }

    const audio = new Audio(voice.sampleAudioUrl);
    audioRef.current = audio;
    audio.play().catch(console.error);
    audio.onended = () => setPlayingId(null);
    setPlayingId(voice.id);
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      audioRef.current?.pause();
    };
  }, []);

  const resetFilters = () => {
    setGender("");
    setTier("");
    setIsPremium("");
    setSearch("");
  };

  // ── Render ──────────────────────────────────────────────────────────────────

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-[#13161B] rounded-2xl w-full max-w-6xl max-h-[90vh] flex flex-col shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-[#22262F]">
          <div>
            <h2 className="text-2xl font-bold text-white">Choose a Voice</h2>
            <p className="text-sm text-gray-400 mt-0.5">
              {loading ? "Loading…" : `${total} voices available`}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-[#22262F] rounded-lg transition-colors text-gray-400 hover:text-white"
          >
            <X size={20} />
          </button>
        </div>

        {/* Filters */}
        <div className="px-6 py-4 border-b border-[#22262F] flex flex-wrap items-center gap-3">
          {/* Search */}
          <div className="relative flex-1 min-w-[200px]">
            <Search
              className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500"
              size={15}
            />
            <input
              type="text"
              placeholder="Search voices…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-9 pr-4 py-2 bg-[#0C0E12] border border-[#22262F] rounded-lg text-white placeholder-gray-600 text-sm focus:outline-none focus:border-purple-500/60 transition-colors"
            />
          </div>

          {/* Language */}
          <select
            value={langCode}
            onChange={(e) => setLangCode(e.target.value)}
            className="px-3 py-2 bg-[#0C0E12] border border-[#22262F] rounded-lg text-white text-sm focus:outline-none focus:border-purple-500/60"
          >
            {LANGUAGES.map((l) => (
              <option key={l.code} value={l.code}>
                {l.label}
              </option>
            ))}
          </select>

          {/* Gender */}
          <select
            value={gender}
            onChange={(e) => setGender(e.target.value)}
            className="px-3 py-2 bg-[#0C0E12] border border-[#22262F] rounded-lg text-white text-sm focus:outline-none focus:border-purple-500/60"
          >
            <option value="">All genders</option>
            <option value="male">Male</option>
            <option value="female">Female</option>
          </select>

          {/* Tier */}
          <select
            value={tier}
            onChange={(e) => setTier(e.target.value)}
            className="px-3 py-2 bg-[#0C0E12] border border-[#22262F] rounded-lg text-white text-sm focus:outline-none focus:border-purple-500/60"
          >
            <option value="">All tiers</option>
            <option value="chirp3">Chirp 3 HD</option>
            <option value="chirp">Chirp HD</option>
            <option value="studio">Studio</option>
            <option value="neural2">Neural2</option>
            <option value="wavenet">WaveNet</option>
            <option value="standard">Standard</option>
          </select>

          {/* Premium toggle */}
          <select
            value={isPremium}
            onChange={(e) => setIsPremium(e.target.value)}
            className="px-3 py-2 bg-[#0C0E12] border border-[#22262F] rounded-lg text-white text-sm focus:outline-none focus:border-purple-500/60"
          >
            <option value="">All voices</option>
            <option value="true">Premium only</option>
            <option value="false">Standard only</option>
          </select>

          <button
            onClick={resetFilters}
            className="px-3 py-2 text-purple-400 hover:text-purple-300 text-sm font-medium transition-colors"
          >
            Reset
          </button>
        </div>

        {/* Grid */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex items-center justify-center h-48 text-gray-500">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-purple-500 mr-3" />
              Loading voices…
            </div>
          ) : voices.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-48 text-gray-500">
              <p className="font-medium">No voices match your filters</p>
              <button
                onClick={resetFilters}
                className="mt-2 text-purple-400 hover:text-purple-300 text-sm"
              >
                Clear filters
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
              {voices.map((voice) => (
                <VoiceCard
                  key={voice.id}
                  voice={voice}
                  onSelect={onSelect}
                  playingId={playingId}
                  onTogglePlay={handleTogglePlay}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default VoiceSelector;
